import { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'

export interface ValuationHistoryEntry {
  id: string
  app_slug: string
  acquisition: { low: number; midpoint: number; high: number; rationale: string }
  one_time_buildout?: { low: number; midpoint: number; high: number; rationale: string }
  monthly_subscription?: { low: number; midpoint: number; high: number; rationale: string }
  annual_license?: { low: number; midpoint: number; high: number; rationale: string }
  pitch_value?: { low: number; midpoint: number; high: number; rationale: string }
  generated_at: string
  model: string
}

export interface ValuationDocHistoryEntry {
  id: string
  app_slug: string
  google_doc_id: string
  google_doc_url: string
  drive_folder_url?: string | null
  brand_key?: string
  generated_at: string
}

export interface GammaArtifactHistoryEntry {
  id: string
  app_slug: string
  kind: 'whitepaper' | 'marketing_deck'
  generation_id: string
  url: string
  created_at: string
}

export interface MarketingDeckHistoryEntry {
  id: string
  app_slug: string
  video_concept?: string
  hook_seconds?: number
  duration_seconds?: number
  gamma_url?: string
  gamma_id?: string
  generated_at: string
}

export interface PortfolioSynergy {
  id: string
  from_slug: string
  from_app_name: string
  to_slug: string
  type: 'reuse' | 'merge' | 'bundle' | 'cross_sell'
  rationale: string
  generated_at: string
  generated_iteration: number
}

export interface AppHistory {
  valuations: ValuationHistoryEntry[]
  valuationDocs: ValuationDocHistoryEntry[]
  gammaArtifacts: GammaArtifactHistoryEntry[]
  marketingDecks: MarketingDeckHistoryEntry[]
  synergies: PortfolioSynergy[]
  loading: boolean
}

function liveCollection<T>(
  setter: (rows: T[]) => void,
  qry: ReturnType<typeof query>,
  onFirstSnapshot?: () => void,
) {
  let first = true
  return onSnapshot(qry, snap => {
    setter(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<T, 'id'>) }) as T))
    if (first) { first = false; onFirstSnapshot?.() }
  }, () => { setter([]); if (first) { first = false; onFirstSnapshot?.() } })
}

/**
 * Live history snapshot for one app: all valuation rows, all branded
 * Google Docs ever generated, all whitepaper/deck Gamma artifacts, all
 * portfolio synergies originating from this app. Multiple Firestore
 * listeners reconcile in real time.
 */
export function useAppHistory(slug: string | undefined): AppHistory {
  const [valuations, setValuations] = useState<ValuationHistoryEntry[]>([])
  const [valuationDocs, setValuationDocs] = useState<ValuationDocHistoryEntry[]>([])
  const [gammaArtifacts, setGammaArtifacts] = useState<GammaArtifactHistoryEntry[]>([])
  const [marketingDecks, setMarketingDecks] = useState<MarketingDeckHistoryEntry[]>([])
  const [synergies, setSynergies] = useState<PortfolioSynergy[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Clear the previous app's data so stale rows don't flash while the new
    // app's snapshots load.
    setValuations([]); setValuationDocs([]); setGammaArtifacts([])
    setMarketingDecks([]); setSynergies([])

    if (!slug) {
      setLoading(false)
      return
    }
    setLoading(true)

    // Only flip loading off once the first snapshot has arrived for every
    // listener (or each errored out), not synchronously on subscribe.
    const TOTAL = 5
    let firstSnapshots = 0
    const markFirst = () => {
      firstSnapshots += 1
      if (firstSnapshots >= TOTAL) setLoading(false)
    }

    const unsubs = [
      liveCollection<ValuationHistoryEntry>(setValuations,
        query(collection(db, 'valuations'), where('app_slug', '==', slug), orderBy('generated_at', 'desc')), markFirst),
      liveCollection<ValuationDocHistoryEntry>(setValuationDocs,
        query(collection(db, 'valuation_docs'), where('app_slug', '==', slug), orderBy('generated_at', 'desc')), markFirst),
      liveCollection<GammaArtifactHistoryEntry>(setGammaArtifacts,
        query(collection(db, 'gamma_artifacts'), where('app_slug', '==', slug), orderBy('created_at', 'desc')), markFirst),
      liveCollection<MarketingDeckHistoryEntry>(setMarketingDecks,
        query(collection(db, 'marketing_decks'), where('app_slug', '==', slug), orderBy('generated_at', 'desc')), markFirst),
      liveCollection<PortfolioSynergy>(setSynergies,
        query(collection(db, 'portfolio_synergies'), where('from_slug', '==', slug), orderBy('generated_at', 'desc')), markFirst),
    ]
    return () => unsubs.forEach(u => u())
  }, [slug])

  return { valuations, valuationDocs, gammaArtifacts, marketingDecks, synergies, loading }
}
